import { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import CardPillLabel from "../../components/ui/CardPillLabel";
import AnimatedUnderline from "../../components/ui/AnimatedUnderline";
import HeroArticle from "../../components/common/HeroArticle";
import ImageModal from "../../features/media/components/ImageModal";
import VideoModal from "../../features/media/components/VideoModal";
import { IconFullscreen, IconOpenExternal, IconFlame, IconCrown, IconLightbulb } from "../../components/ui/Icons";

import videoCoverImg from "../../assets/placeholders/CoverVideo.png";
import starozytnaGrecjaPostacImg from "../../assets/placeholders/StarozytynaGracjaPostac.jpg";
import mieszkoPostacImg from "../../assets/placeholders/MieszkoPostac.jpg";
import polskaPostacImg from "../../assets/placeholders/PolskaPostac.jpg";
import starozytnyRzymPostacImg from "../../assets/placeholders/StarozytynaRzymPostac.jpg";
import krucjatyPostacImg from "../../assets/placeholders/KrucjatyPostac.jpg";
import kazimierzPostacImg from "../../assets/placeholders/KazimierzPostac.jpg";
import rycerzPostacImg from "../../assets/placeholders/RycerzPotac.jpg";
import szlachtaPostacImg from "../../assets/placeholders/SzlachtaPostac.jpg";
import soliderBoyImg from "../../assets/placeholders/SoliderBoy.png";

const videoPlaceholderSrc = videoCoverImg;
const demoVideoSrc =
  "https://pub-d1390f28d2f045c8b066c892298b592e.r2.dev/KerinPresentation.mp4";

const historySlides = [
  {
    src: starozytnaGrecjaPostacImg,
    alt: "Starożytna Grecja i Persja",
    title: "Starożytna Grecja i Persja",
    body:
      "Przenosimy się do świata antyku. Udamy się do Aten, na perski dwór Kserksesa, do spartańskiej szkoły, na pola bitwy pod Termopilami oraz do Macedonii. Barwną i ciekawą opowieść o fundamentach kultury europejskiej współtworzą uczniowie, odgrywając role w poszczególnych scenkach.",
  },
  {
    src: mieszkoPostacImg,
    alt: "Mieszko i początki państwa",
    title: "Mieszko i początki państwa",
    body:
      "W roku 966 Mieszko I przyjął chrzest i otworzył Polskę na nowy świat. Pierwsze plemiona zjednoczyły się pod jednym władcą, a kraj zaczął istnieć na mapach Europy. To czas trudnych wyborów, sojuszy i walk o przetrwanie młodego państwa pośród wrogów z każdej strony.",
  },
  {
    src: polskaPostacImg,
    alt: "Polska i jej dzieje",
    title: "Polska i jej dzieje",
    body:
      "Polska to kraj, który wielokrotnie znikał z map i odradzał się na nowo. Przez wieki kształtowały ją bitwy, traktaty, wielcy królowie i zwykli ludzie gotowi poświęcić wszystko. To historia pełna chwały, upadków i nieustannego dążenia do wolności.",
  },
  {
    src: starozytnyRzymPostacImg,
    alt: "Starożytny Rzym",
    title: "Starożytny Rzym",
    body:
      "Od małej osady nad Tybrem wyrósł Rzym na imperium, które rządziło światem przez stulecia. Legiony, prawo, akwedukty i areny gladiatorów to tylko część tej niezwykłej opowieści. Uczniowie odwiedzają cesarski dwór, zasiadają w senacie i poznają codzienność ludzi, którzy stworzyli podwaliny Europy.",
  },
  {
    src: krucjatyPostacImg,
    alt: "Krucjaty",
    title: "Krucjaty",
    body:
      "W imię wiary rycerze z całej Europy ruszyli w długą drogę na Wschód. Krucjaty to czas bohaterstwa, ale też zderzenia kultur i wielkich ambicji. Za żelazną zbroją kryły się marzenia o chwale, a niekiedy o czymś znacznie prostszym — o przetrwaniu kolejnego dnia.",
  },
  {
    src: kazimierzPostacImg,
    alt: "Kazimierz i średniowiecze",
    title: "Kazimierz i średniowiecze",
    body:
      "Kazimierz Wielki przejął Polskę drewnianą, a zostawił murowaną. Wznosił zamki, nadawał prawa i sprowadzał uczonych z całej Europy. Jego panowanie to złoty czas polskiego średniowiecza, gdy kraj kwitł, a Kraków stawał się jednym z najpiękniejszych miast kontynentu.",
  },
  {
    src: rycerzPostacImg,
    alt: "Rycerz",
    title: "Świat rycerzy",
    body:
      "Rycerz to nie tylko stal i miecz, lecz przede wszystkim kodeks, honor i gotowość do poświęcenia. Całymi latami ćwiczył fechtunek, ujeżdżanie konia i walkę w ciężkiej zbroi. W Grodzie Żywej Historii ten świat nie jest tylko legendą — można go dotknąć i samemu poczuć jego ciężar.",
  },
  {
    src: szlachtaPostacImg,
    alt: "Szlachta",
    title: "Szlachta i dawne obyczaje",
    body:
      "Szlachcic był panem swojej ziemi, swojego głosu i swojej szabli. Polskie zwyczaje szlacheckie pełne były ceremonii, uczt i niepisanych zasad przekazywanych z pokolenia na pokolenie. To epoka kontuszów, sejmikowania i dumy z wolności, która była dla szlachty wartością ponad wszystko.",
  },
];

const aboutSection = {
  title: "O nas",
  text:
    "Szkoła Rycerska Kerin to miejsce, w którym nauka staje się prawdziwą przygodą. W przestrzeni Grodu Żywej Historii, greckiego amfiteatru, rzymskiego koloseum, zbrojowni i pracowni rzemieślniczych uczniowie poznają świat przez działanie, emocje i doświadczenie. Historia nie jest tu jedynie opowieścią, lecz czymś, w co można wejść całym sobą, zakładając zbroję, odgrywając role i ucząc się w autentycznych sceneriach. Równie ważne są sztuka, muzyka, podróże, nauka o świecie i rozwój osobisty. Uczniowie odkrywają geografię w praktyce, uczestniczą w warsztatach biologicznych, biorą udział w wyjazdach, rejsach i zajęciach terenowych, a także rozwijają talenty w obszarach takich jak rzemiosło, fotografia, bushcraft, jazda konna, fechtunek, retoryka czy wolontariat. To edukacja, która łączy wiedzę, charakter, sprawczość i odwagę, pomagając każdemu dziecku odnaleźć własną drogę rozwoju.",
};

const learningAreas = [
  {
    title: "Żywa historia i działanie",
    text:
      "Nauka odbywa się przez doświadczenie, role, ruch i autentyczne przestrzenie, które angażują uczniów znacznie mocniej niż tradycyjne lekcje.",
  },
  {
    title: "Nauka, podróże i odkrywanie świata",
    text:
      "Geografia, biologia, wyjazdy, warsztaty i spotkania z pasjonatami pomagają poznawać świat w praktyce i rozbudzają naturalną ciekawość.",
  },
  {
    title: "Talenty, charakter i rozwój",
    text:
      "Sztuka, rzemiosło, aktywność fizyczna i projekty wspierają rozwój pasji, samodzielności, odwagi i mocnych stron każdego ucznia.",
  },
];

export default function KerinHistoriaMisja() {
  const [openImg, setOpenImg] = useState(null);
  const [videoOpen, setVideoOpen] = useState(false);
  const [loadVideo, setLoadVideo] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const videoWrapRef = useRef(null);

  useEffect(() => {
    const element = videoWrapRef.current;
    if (!element) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first?.isIntersecting) {
          setLoadVideo(true);
          observer.disconnect();
        }
      },
      { root: null, threshold: 0.15 }
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const currentSlide = useMemo(
    () => historySlides[activeSlide],
    [activeSlide]
  );

  const goPrevSlide = () => {
    setActiveSlide((prev) =>
      prev === 0 ? historySlides.length - 1 : prev - 1
    );
  };

  const goNextSlide = () => {
    setActiveSlide((prev) =>
      prev === historySlides.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <section className="pageSection homeSection">
      <div className="container">
        <div className="homeHeroIntro">
          <div className="homeHeroIntroInner">
            <div className="homeHeroKicker">Kerin, historia i misja</div>

            <h1 className="homeHeroTitle">
              <span
                className="homeHeroTitleBlue"
                style={{ color: "var(--accent, #b409ab)" }}
              >
                Szkoła Rycerska
              </span>{" "}
              <span className="homeHeroTitleDark">Kerin</span>
            </h1>

            <div className="homeHeroBadge">
              Miejsce, w którym historia ożywa, talenty nabierają siły, a nauka
              staje się przygodą pełną działania, emocji i prawdziwego
              zaangażowania.
            </div>
          </div>
        </div>

        <div className="grid2">
          <div className="leftCol">
            <div className="card cardLabeled">
              <CardPillLabel text="O szkole" variant="miejsca" />
              <h1 className="h1">Edukacja, która zostaje w pamięci</h1>

              <p className="mutedSmall" style={{ marginTop: 6 }}>
                Szkoła Rycerska Kerin to przestrzeń dla uczniów, którzy chcą
                poznawać świat aktywnie, twórczo i z odwagą. Łączymy historię,
                sztukę, naukę, podróże i rozwój osobisty, pokazując, że edukacja
                może być inspirującym doświadczeniem każdego dnia.
              </p>

              <div className="statusRow">
                <AnimatedUnderline as="span" className="pill ok" delay={0.1}>Gród Żywej Historii</AnimatedUnderline>
                <AnimatedUnderline as="span" className="pill" delay={0.2}>Nauka przez doświadczenie</AnimatedUnderline>
                <AnimatedUnderline as="span" className="pill" delay={0.3}>Rozwój talentów i charakteru</AnimatedUnderline>
              </div>
            </div>

            <HeroArticle
              imgSrc={soliderBoyImg}
              tagText="KERIN, HISTORIA I MISJA"
              title="Miejsce, w którym nauka staje się przygodą"
              titleIcon={<span style={{ color: "#f59e0b" }}><IconLightbulb /></span>}
              subtitle="Nowoczesna edukacja osadzona w żywej historii"
              body="Nasz Gród Żywej Historii wyposażony w amfiteatr grecki, koloseum rzymskie, zbrojownię oraz stanowiska rzemieślnicze pozwala doświadczać wiedzy w praktyce. Curioni uczą się przez udział, współtworzenie i spotkania z pasjonatami, którzy potrafią rozbudzić ciekawość świata."
              childrenBelow={
                <>
                  <div className="infoGrid">
                    {[
                      {
                        label: "Nasza misja",
                        value: "Tworzymy środowisko, w którym dziecko rozwija wiedzę, charakter, sprawczość i odwagę do działania.",
                        Icon: IconFlame,
                        iconColor: "#dc6803",
                      },
                      {
                        label: "Nasza wyjątkowość",
                        value: "Łączymy autentyczne przestrzenie, projekty edukacyjne, rzemiosło, sztukę i doświadczenie w jedną spójną, inspirującą drogę rozwoju.",
                        Icon: IconCrown,
                        iconColor: "#7c3aed",
                      },
                    ].map(({ label, value, Icon, iconColor }, index) => (
                      <motion.div
                        key={label}
                        className="infoBox"
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ duration: 1.4, ease: "easeOut", delay: index * 0.12 }}
                      >
                        <span className="infoBoxIcon" style={{ color: iconColor }}><Icon /></span>
                        <div className="infoLabel">{label}</div>
                        <div className="infoValue">{value}</div>
                      </motion.div>
                    ))}
                  </div>
                </>
              }
            />

            <div className="card cardLabeled">
              <CardPillLabel text="Najważniejsze obszary" variant="lista" />

              <div className="missionSections">
                <section className="missionIntroBlock">
                  <div className="missionIntroTop">
                    <h2 className="h2 missionIntroTitle">
                      {aboutSection.title}
                    </h2>
                  </div>

                  <p className="missionIntroText">{aboutSection.text}</p>
                </section>

                <section className="missionAreasBlock">
                  <div className="missionIntroTop">
                    <h2 className="h2 missionIntroTitle">
                      Co tworzy doświadczenie Kerin
                    </h2>
                  </div>

                  <div className="missionAreaList">
                    {learningAreas.map((item, index) => (
                      <motion.article
                        className="missionAreaItem"
                        key={item.title}
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true, amount: 0.2 }}
                        transition={{ duration: 1.4, ease: "easeOut", delay: index * 0.12 }}
                      >
                        <h3 className="missionAreaTitle">{item.title}</h3>
                        <p className="missionAreaText">{item.text}</p>
                      </motion.article>
                    ))}
                  </div>
                </section>
              </div>
            </div>

            <div className="card cardLabeled">
              <CardPillLabel text="Film" variant="film" />

              <div
                className="videoWrap"
                ref={videoWrapRef}
                style={{ marginTop: 6 }}
              >
                {!loadVideo ? (
                  <div
                    className="videoPlaceholder"
                    role="status"
                    aria-label="Ładowanie filmu"
                  >
                    Film ładuje się, przewiń kawałek niżej lub chwilę poczekaj
                  </div>
                ) : (
                  <video
                    className="videoPlayer"
                    src={demoVideoSrc}
                    controls
                    playsInline
                    preload="metadata"
                    poster={videoPlaceholderSrc}
                  >
                    Twoja przeglądarka nie wspiera odtwarzania wideo.
                  </video>
                )}
              </div>

              <div className="row gap wrap" style={{ marginTop: 10 }}>
                <button
                  type="button"
                  className="btn btnIcon"
                  onClick={() => setVideoOpen(true)}
                >
                  <IconFullscreen />
                  Otwórz film na pełnym ekranie
                </button>

                {/* <a
                  className="btn btnIcon"
                  href={demoVideoSrc}
                  target="_blank"
                  rel="noreferrer"
                >
                  <IconOpenExternal />
                  Otwórz film w nowej karcie
                </a> */}
              </div>
            </div>

            <div className="card cardLabeled">
              <CardPillLabel text="Epoki i opowieści" variant="miejsca" />

              <h2 className="h2" style={{ marginTop: 8 }}>
                Poznaj kolejne światy Kerin
              </h2>

              <p className="mutedSmall" style={{ marginTop: 6 }}>
                Wejdź w świat opowieści zapisanych w obrazach. Przeglądaj zdjęcia, odkrywaj historie i poznawaj ducha miejsca ukrytego w detalach.
              </p>

              <div className="historySliderSection" style={{ marginTop: 18 }}>
                <button
                  type="button"
                   className="imgBtn slideImageWrap"
                  onClick={() =>
                    setOpenImg({
                      src: currentSlide.src,
                      alt: currentSlide.alt,
                    })
                  }
                  aria-label={`Otwórz zdjęcie: ${currentSlide.alt}`}
                >
                  <img
                    className="slideImage"
                    src={currentSlide.src}
                    alt={currentSlide.alt}
                  />
                </button>

                <div className="historySlideContent">
                  <h3 className="h2 historySlideTitle">{currentSlide.title}</h3>

                  <p className="mutedSmall historySlideText">
                    {currentSlide.body}
                  </p>
                </div>

                <div
                  className="row gap wrap"
                  style={{
                    justifyContent: "center",
                    marginTop: 18,
                  }}
                >
                  <button type="button" className="btn" onClick={goPrevSlide}>
                    Poprzednie
                  </button>

                  <span className="pill">
                    {activeSlide + 1} / {historySlides.length}
                  </span>

                  <button type="button" className="btn" onClick={goNextSlide}>
                    Następne
                  </button>
                </div>
              </div>
            </div>

            <div className="card cardLabeled">
              <CardPillLabel text="Dlaczego warto" variant="lista" />
              <h2 className="h2" style={{ marginTop: 8 }}>
                Szkoła, która rozwija więcej niż wiedzę
              </h2>

              <div style={{ marginTop: 12 }}>
                <p className="mutedSmall" style={{ marginBottom: 10 }}>
                  Uczymy przez projekty, które łączą historię, rzemiosło,
                  języki, sztukę, podróże i współpracę. Dzięki temu uczniowie
                  nie tylko zdobywają wiedzę, ale uczą się samodzielności,
                  odpowiedzialności i odwagi.
                </p>

                <p className="mutedSmall" style={{ marginBottom: 10 }}>
                  Program rozwija się etapami, od pierwszych doświadczeń po
                  coraz większą swobodę działania i świadome odkrywanie własnych
                  talentów.
                </p>

                <p className="mutedSmall" style={{ marginBottom: 0 }}>
                  To edukacja, która daje przestrzeń do wzrastania, pokazuje
                  sens nauki i buduje motywację, która zostaje z dzieckiem na
                  dłużej.
                </p>
              </div>
            </div>
          </div>

          <div className="rightSticky">
            <div className="card cardLabeled">
              <CardPillLabel text="Wyjątkowe doświadczenia" variant="miejsca" />

              <div style={{ marginTop: 8 }}>
                <div className="routeList" role="list">
                  {[
                    "Grecki amfiteatr i rzymskie koloseum",
                    "Ogromna zbrojownia pełna historycznych replik",
                    "Kerinalia, koncerty i pokazy uczniów",
                    "Warsztaty biologiczne z chirurgiem",
                    "Żeglarstwo, rejsy i nauka nawigacji",
                    "Wyjazdy do pałaców, muzeów i laboratoriów",
                    "Rzemiosło, fechtunek, bushcraft i fotografia",
                    "Indywidualna ścieżka rozwoju dla każdego dziecka",
                  ].map((item, index) => (
                    <AnimatedUnderline
                      as="div"
                      className="routeRow"
                      role="listitem"
                      key={item}
                      inner
                      delay={index * 0.08}
                    >
                      <div className="routeIdx">{index + 1}</div>
                      <div className="routeMain">
                        <div className="routeTitle">{item}</div>
                      </div>
                    </AnimatedUnderline>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {openImg && (
        <ImageModal
          src={openImg.src}
          alt={openImg.alt}
          onClose={() => setOpenImg(null)}
        />
      )}

      {videoOpen && (
        <VideoModal
          src={demoVideoSrc}
          onClose={() => setVideoOpen(false)}
        />
      )}
    </section>
  );
}